import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

// Validações por conta (contabil_validacoes) do período e fechamentos
// (contabil_fechamentos) da empresa. Fonte única para a aba Balancete e para o
// assistente Fechamento do mês: as duas telas recebem a mesma instância.
//
// Funções de gravação devolvem { ok: true } ou { ok: false } e registram o erro
// técnico no console; quem chama mostra a mensagem para a tela.

const VAZIO = Object.freeze([]);

// Coluna `totais` vem da migração 030. Antes dela, o upsert com o campo é
// recusado pelo PostgREST; grava de novo sem ele.
const faltaColunaTotais = (err) => !!err && /totais/i.test(`${err.message || ''} ${err.details || ''} ${err.hint || ''}`);

async function upsertValidacoes(registros) {
  let { error } = await supabase.from('contabil_validacoes')
    .upsert(registros, { onConflict: 'empresa_slug,periodo,conta_codigo' });
  if (error && faltaColunaTotais(error)) {
    ({ error } = await supabase.from('contabil_validacoes')
      .upsert(registros.map(({ totais, ...r }) => r), { onConflict: 'empresa_slug,periodo,conta_codigo' }));
  }
  if (error) throw error;
}

async function usuarioAtual() {
  const { data } = await supabase.auth.getUser();
  return data?.user || null;
}

export function useContabilFechamento(slug, periodo) {
  const [estado, setEstado] = useState({ chave: null, validacoes: VAZIO, fechamentos: VAZIO, erro: null, carregando: false });
  const seqRef = useRef(0);
  const chave = `${slug || ''}|${periodo || ''}`;

  const carregar = useCallback(async () => {
    if (!slug || !periodo) return;
    const seq = ++seqRef.current;
    setEstado(prev => ({ ...prev, carregando: true }));
    try {
      const [val, fec] = await Promise.all([
        // '*' em vez da lista: funciona com e sem a coluna `totais` (migração 030).
        supabase.from('contabil_validacoes').select('*').eq('empresa_slug', slug).eq('periodo', periodo),
        supabase.from('contabil_fechamentos').select('periodo, snapshot, fechado_por, fechado_em').eq('empresa_slug', slug),
      ]);
      const erro = val.error || fec.error;
      if (erro) throw erro;
      if (seq !== seqRef.current) return;
      setEstado({ chave: `${slug}|${periodo}`, validacoes: val.data || [], fechamentos: fec.data || [], erro: null, carregando: false });
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('[contabilidade] Falha ao carregar validações e fechamentos.', err);
      setEstado({ chave: `${slug}|${periodo}`, validacoes: VAZIO, fechamentos: VAZIO, erro: err, carregando: false });
    }
  }, [slug, periodo]);

  useEffect(() => { carregar(); }, [carregar]);

  const executar = useCallback(async (rotulo, fn) => {
    try {
      await fn();
      await carregar();
      return { ok: true };
    } catch (err) {
      console.error(`[contabilidade] Falha ao ${rotulo}.`, err);
      await carregar();
      return { ok: false, erro: err };
    }
  }, [carregar]);

  // totais = foto da linha do balancete (conferencia.totaisDaLinha).
  const validar = useCallback((conta, { observacao, totais } = {}) => executar('validar a conta', async () => {
    const user = await usuarioAtual();
    await upsertValidacoes([{
      empresa_slug: slug, periodo, conta_codigo: conta, status: 'validado',
      // Com a migração 030 o servidor carimba data e usuário; antes dela vale o do navegador.
      validado_por: user?.id || null, validado_em: new Date().toISOString(),
      observacao: observacao?.trim() || null, totais: totais || null,
    }]);
  }), [executar, slug, periodo]);

  const desfazer = useCallback((conta, { observacao } = {}) => executar('desfazer a validação', async () => {
    await upsertValidacoes([{
      empresa_slug: slug, periodo, conta_codigo: conta, status: 'pendente',
      validado_por: null, validado_em: null, observacao: observacao?.trim() || null, totais: null,
    }]);
  }), [executar, slug, periodo]);

  // itens: [{ conta, totais }] — validações trazidas do formato antigo.
  const importarAntigas = useCallback((itens) => executar('trazer as validações anteriores', async () => {
    const user = await usuarioAtual();
    const agora = new Date().toISOString();
    await upsertValidacoes(itens.map(({ conta, totais }) => ({
      empresa_slug: slug, periodo, conta_codigo: conta, status: 'validado',
      validado_por: user?.id || null, validado_em: agora,
      observacao: 'Validação trazida do balancete anterior.', totais: totais || null,
    })));
  }), [executar, slug, periodo]);

  const fechar = useCallback((snapshot) => executar('fechar o período', async () => {
    const user = await usuarioAtual();
    const { error } = await supabase.from('contabil_fechamentos').insert({
      empresa_slug: slug, periodo,
      snapshot: { ...snapshot, fechadoPor: user ? { id: user.id, email: user.email || null } : null },
    });
    if (error) throw error;
  }), [executar, slug, periodo]);

  // snapshotAntigo: fechamento feito pela tela antiga (contabilConfig.balancetesFechados).
  const reabrir = useCallback((justificativa, snapshotAntigo) => executar('reabrir o período', async () => {
    const { error } = await supabase.rpc('contabil_reabrir_periodo', {
      p_empresa_slug: slug, p_periodo: periodo, p_justificativa: justificativa, p_snapshot_antigo: snapshotAntigo || null,
    });
    if (error) throw error;
  }), [executar, slug, periodo]);

  const atual = estado.chave === chave;
  return {
    validacoes: atual ? estado.validacoes : VAZIO,
    fechamentos: atual ? estado.fechamentos : VAZIO,
    erro: atual ? estado.erro : null,
    carregando: estado.carregando || !atual,
    recarregar: carregar,
    validar, desfazer, importarAntigas, fechar, reabrir,
  };
}

export default useContabilFechamento;
